// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — starting in the right language, following the account's, and the person's own
// choice over both.
//
// See `app/mobile/i18n.ts` for the rules. This module only reads the page, keeps the two remembered
// values in localStorage and marks <html lang>:
//
// - `pqf.mobile.language` — the ACCOUNT's language as PQFORCE last stated it (absent: states none).
// - `pqf.mobile.languageChoice` — what the person picked in the account sheet: `de`, `en`, or absent
//   for "from PQFORCE". A choice wins over the account, survives a reload and a re-pairing, and is
//   sent to the API (`X-Pqf-Language`), so the server's own sentences and the tenant's state names
//   follow it too.
// ─────────────────────────────────────────────────────────────────────────────

import {
    type AppLanguage,
    type LanguageChoice,
    appLanguageOf,
    effectiveLanguage,
    languageChoiceOf,
    loadStrings,
    setLanguage,
} from "home/app/mobile/i18n";

const LANGUAGE_KEY = "pqf.mobile.language";
const CHOICE_KEY = "pqf.mobile.languageChoice";

/** The phone's own language, as the page decided it from `Accept-Language`. */
let phoneLanguage: AppLanguage = "en";
/** The account's language, as PQFORCE last stated it — null while it states none. */
let accountLanguage: AppLanguage | null = null;
let choice: LanguageChoice = "auto";

function read(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null; // private mode: nothing survives, which is the phone's language until `me` answers
    }
}

function write(key: string, value: string | null): void {
    try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
    } catch {
        /* private mode: it is still applied for this session */
    }
}

function applyLanguage(language: AppLanguage): void {
    setLanguage(language);
    // Screen readers pick their voice from this; hyphenation and quotes follow it too.
    document.documentElement.lang = language;
}

/** Load the embedded strings and start in this device's language. Before the first render. */
export function initLanguage(root: HTMLElement): AppLanguage {
    let strings: unknown = null;
    try {
        strings = JSON.parse(root.dataset.strings || "null");
    } catch {
        strings = null; // the code's own English, everywhere
    }
    loadStrings(strings);

    phoneLanguage = appLanguageOf(root.dataset.lang);
    const stored = read(LANGUAGE_KEY);
    // The stored one is the ACCOUNT's, learned on an earlier start — without it a German user on an
    // English phone would see an English first frame every time until `me` answered.
    accountLanguage = stored === "de" || stored === "en" ? stored : null;
    choice = languageChoiceOf(read(CHOICE_KEY));

    const language = effectiveLanguage(choice, accountLanguage, phoneLanguage);
    applyLanguage(language);

    return language;
}

/** What the person chose on this device — `auto` unless they picked one. */
export function languageChoice(): LanguageChoice {
    return choice;
}

/** The language to ask the API for, or null to let it use the account's (the `auto` case). */
export function requestedLanguage(): AppLanguage | null {
    return choice === "auto" ? null : choice;
}

/**
 * The account's language, now that the profile has said it. Remembered, so the next start opens in
 * it rather than in the phone's. Answers whether the language on screen changed, so the caller
 * re-renders — which it never does while the person's own choice overrides the account.
 *
 * ⚠ A profile that names NO language means the account states none, and the phone decides — the
 * same rule as before sign-in. The remembered value is FORGOTTEN then, not kept: it came from an
 * earlier answer, and until 2026-09-23 the server read an account without a language as English,
 * so keeping it would pin exactly that answer on this phone for good.
 */
export function followAccountLanguage(language: string | undefined, current: AppLanguage): boolean {
    accountLanguage = language === "de" || language === "en" ? language : null;
    write(LANGUAGE_KEY, accountLanguage);

    const next = effectiveLanguage(choice, accountLanguage, phoneLanguage);
    if (next === current) return false;

    applyLanguage(next);
    return true;
}

/**
 * The person's own choice, from the account sheet. Remembered on the device; `auto` goes back to the
 * account's language. Answers whether the language on screen changed.
 */
export function setLanguageChoice(next: LanguageChoice, current: AppLanguage): boolean {
    choice = next;
    write(CHOICE_KEY, next === "auto" ? null : next);

    const language = effectiveLanguage(choice, accountLanguage, phoneLanguage);
    if (language === current) return false;

    applyLanguage(language);
    return true;
}
